type LocalizedText = Partial<Record<'ko' | 'en' | 'jp' | 'de' | 'fr' | 'es', string | null>>;

export type EunpyeongCareCoupon = {
  id: string;
  code: 'EUNPYEONGCARE';
  name: LocalizedText;
  description: LocalizedText | null;
  type: 'subscription' | 'discount';
  value: number;
  timeUnit: 'day' | 'month' | 'year' | null;
  maxUses: number;
  uses: number;
  remainingUses: number | null;
  isUnlimited: boolean;
  expiresAt: string | null;
  isExpired: boolean;
  createdAt: string;
};

export class CouponDashboardError extends Error {
  constructor(public kind: 'not-found' | 'unavailable' | 'invalid-response') {
    super(kind);
    this.name = 'CouponDashboardError';
  }
}

export function localizedCouponText(text: LocalizedText | null, fallback = ''): string {
  if (!text) return fallback;
  for (const language of ['ko', 'en', 'jp', 'de', 'fr', 'es'] as const) {
    const value = text[language]?.trim();
    if (value) return value;
  }
  return fallback;
}

function isLocalizedText(value: unknown): value is LocalizedText {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.values(value).every((item) => item === null || typeof item === 'string')
  );
}

function isCoupon(value: unknown): value is EunpyeongCareCoupon {
  if (!value || typeof value !== 'object') return false;
  const coupon = value as EunpyeongCareCoupon;
  const isCount = (count: unknown) => typeof count === 'number' && Number.isSafeInteger(count) && count >= 0;
  const isDate = (date: unknown) => typeof date === 'string' && Number.isFinite(Date.parse(date));
  return (
    coupon.code === 'EUNPYEONGCARE' &&
    typeof coupon.id === 'string' &&
    isLocalizedText(coupon.name) &&
    (coupon.description === null || isLocalizedText(coupon.description)) &&
    ['subscription', 'discount'].includes(coupon.type) &&
    typeof coupon.value === 'number' &&
    Number.isFinite(coupon.value) &&
    coupon.value >= 0 &&
    (coupon.type === 'discount'
      ? coupon.timeUnit === null
      : ['day', 'month', 'year'].includes(coupon.timeUnit ?? '')) &&
    isCount(coupon.uses) &&
    isCount(coupon.maxUses) &&
    typeof coupon.isUnlimited === 'boolean' &&
    coupon.isUnlimited === (coupon.maxUses === 0) &&
    (coupon.isUnlimited
      ? coupon.remainingUses === null
      : isCount(coupon.remainingUses) && coupon.remainingUses === Math.max(0, coupon.maxUses - coupon.uses)) &&
    typeof coupon.isExpired === 'boolean' &&
    (coupon.expiresAt === null || isDate(coupon.expiresAt)) &&
    isDate(coupon.createdAt)
  );
}

/** Public GET only. Deliberately independent of AuthAxios and its token/refresh interceptors. */
export async function fetchEunpyeongCareCoupon(signal?: AbortSignal): Promise<EunpyeongCareCoupon> {
  const endpoint = process.env.REACT_APP_ENDPOINT?.replace(/\/+$/, '');
  if (!endpoint) throw new CouponDashboardError('unavailable');

  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  if (signal?.aborted) controller.abort();
  const timeout = setTimeout(abort, 15000);

  try {
    const response = await fetch(`${endpoint}/coupon/eunpyeongcare`, {
      method: 'GET',
      credentials: 'omit',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      // A missing API route is also HTTP 404; do not mistake it for an unissued coupon.
      const isMissingCoupon =
        response.status === 404 && body?.message === 'Published Coupon was not found. [code: EUNPYEONGCARE]';
      throw new CouponDashboardError(isMissingCoupon ? 'not-found' : 'unavailable');
    }
    const coupon: unknown = await response.json();
    if (!isCoupon(coupon)) throw new CouponDashboardError('invalid-response');
    return coupon;
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', abort);
  }
}
